import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Package, CheckCircle, AlertTriangle, Wrench, Clock, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../services/api';
import { StatusBadge, TIPO_MOV_CONFIG } from './estoque.utils';

const OBRA_COLORS = {
  'Benjamin Constant': '#1B3F5E',
  'Lumière': '#4A8FBF',
  'Escritório': '#28A745',
  'Manutenção': '#F5A623',
  'Ingarden': '#6f42c1',
  'Externo': '#fd7e14',
};

function KpiCard({ title, value, icon: Icon, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    green: 'bg-green-50 text-green-700 border-green-100',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-100',
    red: 'bg-red-50 text-red-700 border-red-100',
    gray: 'bg-gray-50 text-gray-600 border-gray-100',
  };
  return (
    <div className={`rounded-xl border p-5 ${colors[color]}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium opacity-80">{title}</span>
        <Icon size={20} className="opacity-60" />
      </div>
      <p className="text-3xl font-bold">{value ?? '—'}</p>
    </div>
  );
}

export default function EstoqueDashboard() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['estoque-dashboard'],
    queryFn: () => api.get('/estoque/dashboard').then(r => r.data.data),
    refetchInterval: 60000,
  });

  async function resolverAlerta(id) {
    await api.put(`/estoque/alertas/${id}/resolver`, { resolvido_por: 'Usuário' });
    qc.invalidateQueries(['estoque-dashboard']);
  }

  if (isLoading) return <div className="p-6 text-gray-400">Carregando...</div>;
  const { totais = {}, por_obra = [], alertas = [], ultimos_movimentos = [], alertas_count = 0 } = data || {};

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Package size={24} className="text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-800">Estoque — Dashboard</h1>
        </div>
        <button onClick={() => navigate('/estoque/itens')}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
          Ver Ferramentas <ArrowRight size={16} />
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <KpiCard title="Total de itens" value={totais.total} icon={Package} color="blue" />
        <KpiCard title="Disponíveis" value={totais.disponivel} icon={CheckCircle} color="green" />
        <KpiCard title="Em uso" value={totais.em_uso} icon={ArrowRight} color="blue" />
        <KpiCard title="Em manutenção" value={totais.manutencao} icon={Wrench} color="yellow" />
        <KpiCard title="Alertas pendentes" value={alertas_count} icon={AlertTriangle} color={alertas_count > 0 ? 'red' : 'gray'} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico por obra */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">Itens por Obra / Local</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={por_obra.map(o => ({ ...o, total: parseInt(o.total) }))} layout="vertical">
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="obra_atual" width={130} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="total" radius={[0, 4, 4, 0]}>
                {por_obra.map((o, i) => (
                  <Cell key={i} fill={OBRA_COLORS[o.obra_atual] || '#94a3b8'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Alertas de manutenção */}
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
              <AlertTriangle size={18} className="text-yellow-500" /> Alertas de Manutenção
            </h2>
            {alertas_count > 0 && (
              <button onClick={() => navigate('/estoque/alertas')} className="text-xs text-blue-600 hover:underline">
                Ver todos ({alertas_count})
              </button>
            )}
          </div>
          {alertas.length === 0 ? (
            <div className="flex flex-col items-center py-8 text-gray-400">
              <CheckCircle size={32} className="text-green-400 mb-2" />
              <p className="text-sm">Nenhum alerta pendente</p>
            </div>
          ) : (
            <div className="space-y-2">
              {alertas.slice(0, 5).map(a => (
                <div key={a.id} className="flex items-start justify-between gap-3 p-3 bg-yellow-50 rounded-xl border border-yellow-100">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{a.item_nome}</p>
                    <p className="text-xs text-gray-500">{a.codigo} · {a.mensagem}</p>
                  </div>
                  <button onClick={() => resolverAlerta(a.id)}
                    className="flex-shrink-0 text-xs px-2 py-1 bg-white border border-yellow-200 rounded-lg hover:bg-yellow-100 text-yellow-700 font-medium">
                    Resolver
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Últimas movimentações */}
      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <Clock size={18} /> Últimas Movimentações
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500 text-xs">
                <th className="pb-2 pr-4">Item</th>
                <th className="pb-2 pr-4">Tipo</th>
                <th className="pb-2 pr-4">Responsável</th>
                <th className="pb-2 pr-4">Destino</th>
                <th className="pb-2">Data</th>
              </tr>
            </thead>
            <tbody>
              {ultimos_movimentos.map(m => {
                const cfg = TIPO_MOV_CONFIG[m.tipo] || { label: m.tipo, color: 'text-gray-500' };
                return (
                  <tr key={m.id} className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/estoque/itens/${m.item_id}`)}>
                    <td className="py-2 pr-4">
                      <p className="font-medium text-gray-800">{m.item_nome}</p>
                      <p className="text-xs text-gray-400">{m.codigo}</p>
                    </td>
                    <td className="py-2 pr-4"><span className={`text-xs font-medium ${cfg.color}`}>{cfg.label}</span></td>
                    <td className="py-2 pr-4 text-gray-600">{m.responsavel}</td>
                    <td className="py-2 pr-4 text-gray-500">{m.obra_destino || '—'}</td>
                    <td className="py-2 text-gray-400 text-xs">
                      {format(new Date(m.created_at), 'dd/MM/yy HH:mm', { locale: ptBR })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
